import { useState } from "react";
import { useAge } from "@/App";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import MagazineStackLoader from "@/components/MagazineStackLoader";
import { Helmet } from "react-helmet-async";

const AgeGate = () => {
  const { verify } = useAge();
  const [declined, setDeclined] = useState(false);

  const handleDecline = () => {
    setDeclined(true);
  };

  if (declined) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center px-6">
        <Helmet><meta name="robots" content="noindex, nofollow" /><title>Age Verification — Kalani Luana</title></Helmet>
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-lipstick-red mx-auto mb-6" />
          <h2 className="font-heading text-2xl text-ivory mb-4">
            Access Restricted
          </h2>
          <p className="font-body text-sand leading-relaxed">
            You must be 18 years or older to access this website. Please return when you meet the age requirement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Age Verification — Kalani Luana</title></Helmet>
      {/* Magazine Stack Animation Background */}
      <MagazineStackLoader />
      
      {/* Age Gate Modal - Higher z-index */}
      <div className="age-gate-modal">
        <div 
          className="age-gate-content max-w-lg w-full mx-6 text-center animate-fade-in"
          data-testid="age-gate-container"
        >
          {/* Logo/Name */}
          <h1 
            className="font-accent text-5xl md:text-6xl text-champagne-gold mb-2 drop-shadow-lg"
            data-testid="age-gate-title"
          >
            Kalani Luana
          </h1>
          
          <p className="font-body text-sm tracking-widest uppercase text-champagne mb-12 drop-shadow-md">
            Luxury Companion
          </p>

          {/* Divider */}
          <div className="w-24 h-px bg-champagne-gold/50 mx-auto mb-12" />

          {/* Warning content */}
          <div className="rounded-2xl p-8 md:p-12 shadow-2xl backdrop-blur-xl bg-charcoal/90 border border-champagne-gold/30">
            <h2 className="font-heading text-2xl md:text-3xl text-ivory mb-6">
              Age Verification Required
            </h2>
            
            <p className="font-body text-sand leading-relaxed mb-8">
              This website contains content intended for adults only. By entering, you confirm that you are at least 18 years of age and legally permitted to view adult content in your jurisdiction.
            </p>

            <div className="space-y-4">
              <Button
                onClick={verify}
                className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-charcoal font-semibold py-6 text-lg"
                data-testid="age-verify-btn"
              >
                I am 18 or older — Enter
              </Button>
              
              <Button
                onClick={handleDecline}
                variant="ghost"
                className="w-full text-sand hover:text-ivory hover:bg-white/10"
                data-testid="age-decline-btn"
              >
                I am under 18 — Exit
              </Button>
            </div>

            <p className="font-body text-xs text-taupe mt-8 leading-relaxed">
              By clicking "Enter," you agree to our Terms of Service and Privacy Policy. 
              This website uses cookies to enhance your experience.
            </p>
          </div>

          {/* Legal disclaimer */}
          <p className="font-body text-xs text-taupe mt-8 max-w-md mx-auto leading-relaxed drop-shadow-md">
            All interactions and arrangements are between consenting adults. 
            Any donations exchanged are for time and companionship only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgeGate;
